import { useMemo, useState } from "react";
import { useEventViewQuery } from "../model/EventView.graphql";
import type { EventFixtureViewFragment } from "../../../types/__generated__/graphql";
import type { EventSwitcherItem } from "../components/EventViewHeader/components/EventSwitcherCard/viewmodel/EventSwitcherCard.viewmodel";

type FixtureLike =
  | {
      urn?: string | null;
      runnerNames?: { home: string; away: string } | null;
    }
  | null
  | undefined;

/**
 * Header label: the shared `sportevent.name`, as the Redux switcher shows it (US sports read
 * `away @ home`), then `home v away` from the runner names, then the view `title`. The warm writes
 * `sportevent.name` too. Order: `event-view-apollo-all-sports.md` §4.5.
 */
const labelFor = (
  fixture: FixtureLike,
  sporteventName: string | null | undefined,
  title: string | null | undefined,
): string => {
  if (sporteventName) return sporteventName;
  if (fixture?.runnerNames) return `${fixture.runnerNames.home} v ${fixture.runnerNames.away}`;

  return title ?? "";
};

export type EventViewFixture = EventFixtureViewFragment["fixture"];

/** `football` → the scoreboard, `base` → the `EventHeader` fallback; the views branch on this alone. */
export type RenderableFixture = { kind: "football" | "base"; urn: string };

/**
 * Football's scoreboard-slot predicate (`scoreboard-slots.{web,native}.tsx`): the only two fixture types
 * it renders; any other sport's renders nothing. Fields, not just `__typename`, for `base`: the read is a
 * deep-partial cast, so another surface's entry can lack the `sportevent.openDate` `EventHeader` reads.
 */
export const renderableFixtureOf = (fixture: EventViewFixture | undefined): RenderableFixture | undefined => {
  if (fixture?.__typename === "FootballFixture") return { kind: "football", urn: fixture.urn };
  if (fixture?.__typename === "BaseFixture" && fixture.sportevent?.openDate != null) {
    return { kind: "base", urn: fixture.urn };
  }
  return undefined;
};

export type EventViewData = {
  urn: string;
  title: string | null;
  label: string;
  headerTheming: string | null;
  // Kind + urn only: the urn-driven scoreboard / EventHeader read their own live data, so the entity
  // (which churns on every tick) never leaves the viewmodel. `undefined` until it is renderable.
  fixture: RenderableFixture | undefined;
  availableToSubscribe: boolean;
  siblings: EventSwitcherItem[];
  // True while only the warm has answered: no `title` and no bell flag yet (see `useEventViewQuery`).
  // Native's latch skips it so a cache reset can't clobber the full record; both fail-opens accept it.
  warmOnly?: boolean;
};

const EMPTY_SIBLINGS: EventSwitcherItem[] = [];

const sameSiblings = (a: EventSwitcherItem[], b: EventSwitcherItem[]) =>
  a.length === b.length &&
  a.every((s, i) => s.viewUrn === b[i].viewUrn && s.viewUrl === b[i].viewUrl && s.label === b[i].label);

function useEventViewData(view: EventFixtureViewFragment | undefined, warmOnly: boolean): EventViewData | null {
  const siblingEventViews = view?.siblingEventViews;
  const nextSiblings = useMemo(
    (): EventSwitcherItem[] =>
      (siblingEventViews ?? [])
        .filter((s): s is NonNullable<typeof s> => s !== null)
        .map((s) => ({
          viewUrn: s.urn,
          viewUrl: s.url,
          label: labelFor(s.fixture, s.sportevent?.name, s.title),
        })),
    [siblingEventViews],
  );

  // Carry the last non-empty sibling list forward: a switch points the query at the sibling's
  // SHALLOW cache entry, which carries no `siblingEventViews` of its own, so the switcher would
  // momentarily see one item and collapse from <Selector> to <PageHeader> — a height shift, and the
  // open drawer torn down mid-animation. Safe because every event in a competition shares the set.
  const [carriedSiblings, setCarriedSiblings] = useState<EventSwitcherItem[]>(nextSiblings);
  // By content: a fixture tick rebuilds `siblingEventViews` (the current event can be its own
  // sibling), and a fresh array here would bust every memo downstream of `siblings`.
  if (nextSiblings.length && !sameSiblings(nextSiblings, carriedSiblings)) {
    setCarriedSiblings(nextSiblings);
  }
  // Only carry the set onto a view the set itself names. An in-place switch always targets one of
  // these urns so it still carries, while a view reached any other way — whose shallow entry this
  // set does not describe — falls back to its own siblings instead of another competition's.
  const canCarry = !!view?.urn && carriedSiblings.some((sibling) => sibling.viewUrn === view.urn);
  const siblings = nextSiblings.length || canCarry ? carriedSiblings : EMPTY_SIBLINGS;

  // `view` changes on every live tick (the fragment spreads `...FootballFixture`), so the record is
  // keyed on the primitives it exposes: native's `latchedData` compares `data` by identity.
  const renderable = renderableFixtureOf(view?.fixture);
  const fixtureKind = renderable?.kind;
  const fixtureUrn = renderable?.urn;
  const fixture = useMemo(
    (): RenderableFixture | undefined =>
      fixtureKind && fixtureUrn ? { kind: fixtureKind, urn: fixtureUrn } : undefined,
    [fixtureKind, fixtureUrn],
  );

  const urn = view?.urn;
  const title = view?.title ?? null;
  const label = view ? labelFor(view.fixture, view.sportevent?.name, view.title) : "";
  const headerTheming = view?.headerTheming ?? null;
  const availableToSubscribe = view?.sportevent?.availableToSubscribe ?? false;

  return useMemo(() => {
    // No entry at all — not even the warm's — so there is nothing to paint. A warmed urn always
    // yields an entry, which then follows the same PageHeader → Selector progression as a cache
    // entry that arrives without its siblings.
    if (!urn) return null;

    return { urn, title, label, headerTheming, fixture, availableToSubscribe, siblings, warmOnly };
  }, [urn, title, label, headerTheming, fixture, availableToSubscribe, siblings, warmOnly]);
}

/**
 * A route-root view is always on-screen while mounted, so `visible` defaults to true; the parameter
 * is kept for the cache-first hook shape. No sport discriminator: the gate decides the partition and
 * hands the shell its sport (`docs/specs/event-view-apollo-all-sports.md` §4.4).
 */
export function useEventViewVM(viewURN: string, visible = true) {
  const {
    loading,
    error,
    refresh,
    warmOnly,
    data: { view },
  } = useEventViewQuery({ viewURN }, { visible });

  const data = useEventViewData(view, warmOnly);

  return {
    loading,
    error,
    refresh,
    vm: {
      data,
    },
  };
}
